<template>
    <div class="container">
        <div v-if="!loader" class="form-bg">
            <!-- Multi-Product Scenario: Show all products -->
            <div v-if="isMultiProductOffer" class="mb-4">
                <div class="price-section">
                    <!-- Show all product thumbnails in one row -->
                    <div class="product-thumbnails">
                        <router-link v-for="(prod, idx) in products" :key="idx"
                            :to="{ name: 'productDetails', params: { id: prod.product ? prod.product.id : prod.id } }">
                            <img v-if="getProductImages(prod).length > 0" class="last-product-thumbnail"
                                :src="storageUrl + getProductImages(prod)[0]" alt="Product" />
                        </router-link>
                    </div>

                    <div class="product-info col col-sm-9">
                        <label class="name">
                            Offer #{{ selectedContact.offer.id }}
                            <!-- Show seller username once for multi-product offers -->
                            <span v-if="getFirstProductUser()" class="text-primary">
                                ({{ getFirstProductUser().username }})
                            </span>
                        </label>
                        <!-- Show all product names -->
                        <div class="product-names-list">
                            <span v-for="(prod, idx) in products" :key="idx" class="product-name-item">
                                {{ getProductName(prod) }}<span v-if="idx < products.length - 1">, </span>
                            </span>
                        </div>
                        <span class="price-number">
                            {{ selectedContact.offer.old_price | currency(' DA', 0, { symbolOnLeft: false }) }}
                        </span>
                    </div>

                    <!-- Status badge if offer is sold -->
                    <div v-if="isOfferSold()" class="action-button">
                        <span class="badge badge-success">{{ $t('sentence.productSold') }}</span>
                    </div>
                </div>

                <!-- Action buttons for multi-product offer -->
                <div class="action-buttons">
                    <router-link v-if="canCheckoutOffer()" class="btn btn-success"
                        :to="{ name: 'checkoutOrder', params: { id: selectedContact.offer.id, type: 'offer' } }">
                        {{ $t ? $t('page.checkout') : 'Checkout' }}
                    </router-link>
                    <button v-else-if="isOfferSold()" disabled class="btn btn-success">
                        {{ $t ? $t('sentence.productSold') : 'Sold' }}
                    </button>
                </div>
            </div>

            <!-- Single Product Scenario: Traditional view for single product -->
            <div v-else class="mb-4">
                <router-link :to="{ name: 'productDetails', params: { id: firstProduct.id } }"
                    class="text-decoration-none">
                    <div class="price-section">
                        <div v-if="firstProduct && firstProduct.pictures && firstProduct.pictures.length" class="">
                            <img class="last-product-thumbnail"
                                :src="storageUrl + (Array.isArray(firstProduct.pictures) ? firstProduct.pictures[0] : firstProduct.pictures)"
                                alt="" />
                        </div>

                        <div class="product-info col col-sm-9">
                            <label class="name">
                                <template>
                                    {{ firstProduct.name }}
                                </template>
                                <span v-if="firstProduct.user && firstProduct.user.id !== user.id" class="text-primary">
                                    ({{ firstProduct.user.username }})
                                </span>
                            </label>
                            <span class="price-number">
                                <template v-if="selectedContact && selectedContact.offer">
                                    {{ selectedContact.product.price | currency(' DA', 0, { symbolOnLeft: false }) }}
                                </template>
                                <template v-else>
                                    {{ product.price | currency(' DA', 0, { symbolOnLeft: false }) }}
                                </template>
                            </span>
                        </div>

                        <!-- Product status badges -->
                        <div v-if="firstProduct.deleted_at !== null" class="action-buttons">
                            <span class="badge badge-danger">{{ $t('status.deleted') }}</span>
                        </div>
                        <div v-else-if="firstProduct.orders && firstProduct.orders.length > 0" class="action-button">
                            <span class="badge badge-success">{{ $t('sentence.productSold') }}</span>
                        </div>
                        <div v-else-if="firstProduct.sales_type == 'auction'" class="action-button">
                            <span class="badge badge-success">
                                <TimerSimpleLong :timeLeft="firstProduct.time_left"
                                    :deadline="firstProduct.validated || 'Null'" :duration="firstProduct.duration"
                                    :isLong="false" />
                            </span>
                        </div>
                    </div>
                </router-link>
            </div>

            <!-- Action buttons for single product -->
            <div v-if="firstProduct.deleted_at !== null" class="action-buttons">
                <!-- No actions for deleted products -->
            </div>
            <div v-else-if="hasAcceptedOffers" class="action-buttons">
                <router-link
                    v-if="firstProduct.orders && firstProduct.orders.length == 0 && acceptedOffer && acceptedOffer.offers && acceptedOffer.offers.user_id === user.id && acceptedOffer.type === 'REQUEST_OFFER'"
                    class="btn btn-success"
                    :to="{ name: 'checkoutOrder', params: { id: acceptedOffer.offers.id, type: 'offer' } }">
                    {{ $t ? $t('page.checkout') : 'Checkout' }}
                </router-link>
                <button v-else-if="selectedContact && selectedContact.product && product.orders.length > 0" disabled
                    class="btn btn-success">
                    {{ $t ? $t('sentence.productSold') : 'Sold' }}
                </button>
            </div>
            <div v-else-if="selectedContact && selectedContact.showOffer" class="action-buttons">
                <!-- Make an Offer Button -->
                <button :disabled="firstProduct.sales_type == 'auction'" v-if="firstProduct.accept_offer === 1"
                    @click="handleMakeOffer" class="btn btn-make-offer">
                    {{ $t('action.makeAnOffer') }}
                </button>

                <!-- Buy Now Button -->
                <button v-if="firstProduct.sales_type !== 'auction'" class="btn btn-buy-now" @click="handleBuyNow">
                    {{ $t('action.buyNow') }}
                </button>
            </div>
            <!-- <div v-else>N/D</div> -->
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import config from "@/config";
import TimerSimpleLong from '../templates/TimerSimpleLong.vue';

export default {
    name: 'OfferBuyNowForm',
    components: {
        TimerSimpleLong,
    },
    props: {
        selectedContact: {
            type: Object
        },
        messages: {
            type: Array,
            default: () => []
        },
        products: {
            type: Array,
            default: () => []
        },
        price: {
            type: [String, Number],
            required: false,
            default: ''
        },
        quantity: {
            type: [String, Number],
            default: 1
        },
        nextPrice: {
            type: [String, Number],
            required: false,
            default: 0
        },
    },
    data() {
        return {
            product: {},
            loader: false,
            storageUrl: config.storageUrl,
            localPrice: '',
            localNextPrice: 0
        };
    },
    mounted() {
        this.localPrice=this.price;
        this.localNextPrice=this.nextPrice;
        this.updateProductFromProps();
    },
    computed: {
        // Determine if this is a multi-product offer - show all products
        isMultiProductOffer() {
            return Array.isArray(this.products)&&this.products.length>1;
        },

        // Alias for the first product object to avoid repeated `products[0].product` usage
        firstProduct() {
            if (!Array.isArray(this.products)||this.products.length===0) return {};
            const p=this.products[0];
            return p.product||p||{};
        },

        quantityProxy: {
            get() {
                return this.quantity;
            },
            set(val) {
                this.$emit('update:quantity', val);
            }
        },

        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        },

        user: {
            get() {
                return this.$store.state.user;
            },
            set(user) {
                this.$store.commit('setUser', { user: user });
            },
        },

        acceptedOffer() {
            if (!this.messages) return null;
            const msgs=Array.isArray(this.messages)? this.messages:[];
            for (let i=0; i<msgs.length; i++) {
                const m=msgs[i];
                if (m&&m.offers&&parseInt(m.offers.status)===1) {
                    return m;
                }
            }
            return null;
        },

        hasAcceptedOffers() {
            return this.acceptedOffer!==null;
        }
    },
    watch: {
        price(newVal) {
            this.localPrice=newVal;
        },
        nextPrice(newVal) {
            this.localNextPrice=newVal;
        },
        products() {
            this.updateProductFromProps();
        },
        selectedContact() {
            this.updateProductFromProps();
        }
    },
    methods: {
        // Helper to get product images from various object structures
        getProductImages(prod) {
            if (!prod) return [];

            // Check if prod has a nested product object
            const productObj=prod.product||prod;

            if (productObj.pictures) {
                return Array.isArray(productObj.pictures)? productObj.pictures:[productObj.pictures];
            }
            return [];
        },

        // Helper to get product name from various object structures
        getProductName(prod) {
            if (!prod) return '';

            // Check if prod has a nested product object
            const productObj=prod.product||prod;

            return productObj.name||'';
        },

        // Get the first product's user for multi-product offers
        getFirstProductUser() {
            if (!this.products||this.products.length===0) return null;

            const firstProd=this.products[0];
            const productObj=firstProd.product||firstProd;

            if (productObj.user&&productObj.user.id!==this.user.id) {
                return productObj.user;
            }
            return null;
        },

        // Check if user can checkout the multi-product offer
        canCheckoutOffer() {
            if (!this.selectedContact||!this.selectedContact.offer) return false;
            if (!this.acceptedOffer||!this.acceptedOffer.offers) return false;

            // Check if current user is the offer owner
            if (this.acceptedOffer.offers.user_id!==this.user.id) return false;

            // Check if offer is accepted and not sold
            if (parseInt(this.acceptedOffer.offers.status)!==1) return false;

            // Check if any product is already sold
            return !this.isOfferSold();
        },

        // Check if any product in the offer is sold
        isOfferSold() {
            if (!this.products||this.products.length===0) return false;

            return this.products.some(prod => {
                const productObj=prod.product||prod;
                return productObj.orders&&productObj.orders.length>0;
            });
        },

        updateProductFromProps() {
            // For multi-product offers, we don't need to set a single product
            if (this.isMultiProductOffer) {
                return;
            }

            // Single product logic
            if (!Array.isArray(this.products)||this.products.length===0) {
                return;
            }

            if (this.selectedContact&&this.selectedContact.product&&this.selectedContact.product.id) {
                const id=this.selectedContact.product.id;
                const found=this.findProductById(id);
                if (found) {
                    this.product=found;
                    return;
                }
                this.product=this.selectedContact.product;
                return;
            }

            if (this.selectedContact&&(this.selectedContact.product_id||this.selectedContact.productId)) {
                const id=this.selectedContact.product_id||this.selectedContact.productId;
                const found=this.findProductById(id);
                if (found) {
                    this.product=found;
                    return;
                }
                this.getProducts(id);
                return;
            }

            if (this.products.length===1) {
                this.product=this.products[0].product||this.products[0];
                return;
            }

            if (this.product&&this.product.id) {
                const found=this.findProductById(this.product.id);
                if (found) {
                    this.product=found;
                }
            }
        },

        findProductById(id) {
            if (!id) return null;
            const pid=parseInt(id, 10);
            if (isNaN(pid)) return null;

            return this.products.find((p) => {
                const productObj=p.product||p;
                return parseInt(productObj.id, 10)===pid;
            })||null;
        },

        acceptedOfferFor(p) {
            if (!this.messages) return null;
            const msgs=Array.isArray(this.messages)? this.messages:[];
            for (let i=0; i<msgs.length; i++) {
                const m=msgs[i];
                if (!m||!m.offers) continue;
                const offers=m.offers;
                if (parseInt(offers.status)!==1) continue;

                const productObj=p.product||p;
                const prodId=offers.product_id||offers.productId||m.product_id||(m.product&&m.product.id)||null;
                if (prodId&&parseInt(prodId)===parseInt(productObj.id)) {
                    return m;
                }
            }
            return null;
        },

        getProducts(id) {
            const self=this;
            this.loader=true;

            Date.prototype.addDays=function (d) {
                return new Date(this.valueOf()+864e5*d);
            };

            return this.$axios({
                url: "product/getProductInfos/"+id,
                method: "GET",
            })
                .then((response) => {
                    self.product=response.data;

                    const bids=Array.isArray(self.product.bids)? self.product.bids:[];

                    self.localPrice=
                        bids.length>0
                            ? bids[0].price
                            :self.product.starting_price;

                    let step=0;
                    switch (true) {
                        case self.localPrice<100:
                            step=5;
                            break;
                        case self.localPrice>=100&&self.localPrice<1000:
                            step=10;
                            break;
                        case self.localPrice>=1000&&self.localPrice<2000:
                            step=50;
                            break;
                        case self.localPrice>=2000&&self.localPrice<10000:
                            step=100;
                            break;
                        default:
                            step=parseFloat(self.localPrice*0.05);
                    }

                    self.localNextPrice=step+parseFloat(self.localPrice);
                    self.$emit('update:price', '');
                    self.$emit('update:nextPrice', self.localNextPrice);
                    self.localPrice="";

                    for (let i=0; i<bids.length; i++) {
                        bids[i].created_at=moment(
                            bids[i].created_at,
                            "YYYY-MM-DD H:m"
                        )
                            .locale("fr")
                            .format("LLL");
                    }

                    const bidderId=self.arrayColumn(bids, "user_id");
                    self.bidders=new Set(bidderId).size;

                    if (self.product.delivery_time!==null) {
                        const today=new Date();
                        self.startShippingDate=today.addDays(self.product.delivery_time);
                        self.endShippingDate=today.addDays(self.product.delivery_time+7);
                        self.startShippingDate=moment(self.startShippingDate, "YYYY-MM-DD").format("MMMM Do YYYY");
                        self.endShippingDate=moment(self.endShippingDate, "YYYY-MM-DD").format("MMMM Do YYYY");
                    }

                    if (Array.isArray(self.product.pictures)) {
                        self.activeSrc=self.product.pictures[0];
                    } else {
                        self.activeSrc=self.product.pictures;
                    }

                    self.$emit('product-loaded', self.product);
                })
                .catch((err) => {
                    console.error(err);
                    throw err;
                })
                .finally(() => {
                    this.loader=false;
                });
        },

        async handleMakeOffer() {
            // Ensure product is available; attempt to resolve and load it from props if missing
            if (!this.product||!this.product.id) {
                let productId=null;

                if (this.selectedContact) {
                    if (this.selectedContact.product&&this.selectedContact.product.id) {
                        productId=this.selectedContact.product.id;
                    } else if (this.selectedContact.product_id||this.selectedContact.productId) {
                        productId=this.selectedContact.product_id||this.selectedContact.productId;
                    } else if (this.selectedContact.offer&&Array.isArray(this.selectedContact.offer.offer_products)&&this.selectedContact.offer.offer_products.length) {
                        const first=this.selectedContact.offer.offer_products[0];
                        productId=(first&&((first.product&&first.product.id)||first.id));
                    }
                }

                // Fall back to single product in `products` prop
                if (!productId&&Array.isArray(this.products)&&this.products.length===1) {
                    this.product=this.products[0].product||this.products[0];
                } else if (!productId&&this.firstProduct&&this.firstProduct.id) {
                    productId=this.firstProduct.id;
                }

                if (productId&&!(this.product&&this.product.id)) {
                    try {
                        await this.getProducts(productId);
                    } catch (err) {
                        // loading failed; continue to notification below
                    }
                }
            }

            if (!this.product||!this.product.id) {
                this.$notify({
                    group: "error",
                    title: this.$t? this.$t("error.title"):"Erreur",
                    text: this.$t? this.$t("api.product_not_loaded"):"Le produit n'est pas encore chargé. Veuillez réessayer.",
                    type: "error",
                });
                if (process&&process.env&&process.env.NODE_ENV!=='production') {
                    console.warn('OfferBuyNowForm: product not loaded when attempting to make an offer');
                }
                return;
            }

            if (!this.isLoggedIn) {
                this.$modal.show('login-user');
                return;
            }

            this.$emit('show-offer-modal', this.product);
            this.$modal.show('make-offer');
        },

        async handleBuyNow() {
            // Handle multi-product batch orders
            if (this.isMultiProductOffer) {
                if (!this.isLoggedIn) {
                    this.$modal.show('login-user');
                    return;
                }

                const productIds=this.products.map(prod => {
                    const productObj=prod.product||prod;
                    return productObj.id;
                }).filter(id => id); // Filter out any undefined IDs

                if (productIds.length===0) {
                    this.$notify({
                        group: "error",
                        title: this.$t? this.$t("error.title"):"Erreur",
                        text: "Aucun produit valide trouvé.",
                        type: "error",
                    });
                    return;
                }

                // Navigate to checkout with batch type
                this.$router.push({
                    name: 'checkoutOrder',
                    params: {
                        type: 'batch',
                        id: productIds.join(',') // Pass comma-separated IDs
                    }
                });
                return;
            }

            // Handle single product checkout
            // Ensure product is loaded before attempting checkout
            if (!this.product||!this.product.id) {
                let productId=null;
                if (this.selectedContact) {
                    productId=this.selectedContact.product&&this.selectedContact.product.id||this.selectedContact.product_id||this.selectedContact.productId||null;
                }
                if (!productId&&this.firstProduct&&this.firstProduct.id) productId=this.firstProduct.id;
                if (productId) {
                    try {
                        await this.getProducts(productId);
                    } catch (e) {
                        // ignore — handled below
                    }
                }
            }

            if (!this.product||!this.product.id) {
                this.$notify({
                    group: "error",
                    title: this.$t? this.$t("error.title"):"Erreur",
                    text: this.$t? this.$t("api.product_not_loaded"):"Le produit n'est pas encore chargé. Veuillez réessayer.",
                    type: "error",
                });
                return;
            }
            const productId=this.product?.product?.id??this.product?.id;

            this.showPopUpConfirmation(1, productId);


        },

        showPopUpConfirmation(id, productId=null) {
            const self=this;

            if (undefined===this.user) {
                self.$modal.show('login-user');
                return;
            }


            // ✅ Use either structure
            const ownerId=this.product?.product?.user_id??this.product?.user_id;

            console.log(ownerId);

            if (this.user.id===ownerId) {
                self.$notify(
                    {
                        group: "error",
                        title: "Erreur !",
                        type: "error",
                        text: self.$t("api.cannot_buy_own_product"),
                    },
                    100000
                );
                return;
            }

            this.$modal.show("dialog", {
                title: "Confirmation",
                text:
                    id===2
                        ? "Vous allez surenchérir sur ce produit."
                        :"Veuillez confimer votre achat",
                buttons: [
                    {
                        title: "Cancel",
                        handler: () => {
                            self.$modal.hide("dialog");
                            return false;
                        },
                    },
                    {
                        title: "Confirmer",
                        handler: () => {
                            self.$modal.hide("dialog");

                            if (id===2) {
                                self.placeBid(id);
                                return;
                            }

                            if (!self.isLoggedIn) {
                                self.$modal.show("login-user");
                                return;
                            }

                            self.$router.push({
                                name: "checkoutOrder",
                                params: {
                                    type: "product",
                                    id: productId,
                                    quantity: self.quantity,
                                },
                            });
                        },
                    },
                ],
            });
        },


        placeBid(id) {
            const self=this;
            self.loader=true;
            self.localPrice=self.localPrice===""? self.localNextPrice:self.localPrice;

            let url, data;

            if (id===1) {
                url="offers/create";
                data={
                    product_id: this.product.id,
                    quantity: this.product.quantity,
                    price: this.product.fixed_price,
                    status: true,
                };
            }

            if (id===2) {
                url="bids/create";
                const bids=Array.isArray(self.product.bids)? self.product.bids:[];
                if (
                    bids.length>0&&
                    parseFloat(self.localPrice)<=parseFloat(bids[0].price)
                ) {
                    self.$notify({
                        group: "error",
                        title: "Erreur !",
                        type: "error",
                        text:
                            "Votre enchère doit dépassé les "+
                            bids[0].price+
                            " DA.",
                    });
                    self.loader=false;
                    return;
                }
                data={
                    product_id: this.product.id,
                    quantity: this.product.quantity,
                    price: this.localPrice,
                };
            }

            this.$axios({
                url: url,
                method: "POST",
                data: data,
            })
                .then((response) => {
                    self.loader=false;

                    if (id===1) {
                        self.$router.push({
                            name: "checkoutOrder",
                            params: {
                                type: "offer",
                                id: response.data,
                            },
                        });
                    }

                    self.getProducts(self.product.id);
                    self.$notify({
                        group: "success",
                        title: "Bravo !",
                        type: "success",
                        text: "Votre action a été enregistrée avec succès!",
                    });
                })
                .catch(function (err) {
                    self.loader=false;

                    if (err.response.status===403) {
                        self.$modal.show("login-user");
                    } else {
                        self.$notify(
                            {
                                group: "error",
                                title: "Erreur !",
                                type: "error",
                                text: self.$t(err.response.data.message),
                            },
                            100000
                        );
                    }
                });
        },

        arrayColumn(arr, key) {
            return arr.map((a) => a[key]);
        },
    }
};
</script>

<style scoped>
.container {
    max-width: 800px;
    margin: 0 auto;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.98);
}

.form-bg {
    background: var(--color-e);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mb-4 {
    margin-bottom: 16px;
}

.label {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-5);
    margin: 0;
}

/* Multi-product offer styles */
.product-thumbnails {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
}

.last-product-thumbnail {
    margin-top: 2px;
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 6px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
}

/* Single product styles */
.price-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.product-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.name {
    font-size: 18px;
    font-weight: 600;
    color: #34495e;
    margin-bottom: 6px;
}

.product-names-list {
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
    line-height: 1.4;
}

.product-name-item {
    display: inline;
}

.price-number {
    font-size: 22px;
    font-weight: bold;
    color: var(--color-secondary);
}

.action-button {
    display: flex;
    align-items: center;
}

.action-buttons {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 24px;
}

.btn {
    flex: 1 1 30%;
    justify-content: center;
}

.btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
    transform: translateY(0);
}

@media (max-width: 767px) {
    .price-section {
        align-items: flex-start;
    }

    .price-number {
        font-size: 20px;
    }

    .form-bg {
        padding: 16px;
        border-radius: 10px;
    }

    .product-thumbnails {
        flex-direction: column;
    }

    .action-buttons {
        gap: 10px;
    }

    .btn {
        padding: 2px !important;
        font-size: 13px;
        height: 52px;
        width: 100%;
    }
}

@media (max-width: 360px) {
    .form-bg {
        padding: 12px;
    }

    .btn {
        font-size: 12px;
        padding: 10px 14px;
    }
}
</style>